import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/landing/Logo";
import { forgotPassword, resetPassword } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { OtpModal } from "@/components/auth/OtpModal";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset session
  const [resetToken, setResetToken] = useState(null);
  const [mobileMasked, setMobileMasked] = useState("");
  const [verifiedCode, setVerifiedCode] = useState(null);

  // UI stage: "request" → "otp" (modal) → "new-password" → "done"
  const [stage, setStage] = useState("request");
  const [otpOpen, setOtpOpen] = useState(false);

  // New password fields
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await forgotPassword(email);
      if (r?.reset_token) {
        setResetToken(r.reset_token);
        setMobileMasked(r.mobile_masked || "");
        setStage("otp");
        setOtpOpen(true);
        toast.success("OTP sent to your registered WhatsApp number");
      } else {
        // For security we don't reveal whether the email exists.
        toast.success("If that email is registered, we've sent a recovery code.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start password reset");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (code) => {
    // We don't actually consume the OTP yet — we hold it in state and send it
    // together with the new password to `/api/auth/reset-password`. That endpoint
    // verifies it atomically with the password update.
    // To still validate the code is shaped properly we just store it.
    if (!/^\d{6}$/.test(code)) throw new Error("Enter the 6-digit code");
    setVerifiedCode(code);
    setOtpOpen(false);
    setStage("new-password");
  };

  const onResendOtp = async () => {
    // Re-request a fresh reset_token + OTP
    const r = await forgotPassword(email);
    if (r?.reset_token) {
      setResetToken(r.reset_token);
      setMobileMasked(r.mobile_masked || "");
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await resetPassword(resetToken, verifiedCode, newPw);
      setStage("done");
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/admin/login", { replace: true }), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not update password";
      toast.error(detail);
      // If the OTP was wrong/expired, take the user back to the OTP step
      if (/otp|expired|incorrect|attempts/i.test(String(detail))) {
        setVerifiedCode(null);
        setStage("otp");
        setOtpOpen(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 px-6 relative overflow-hidden" data-testid="forgot-password-page">
      <div className="absolute -top-32 -left-20 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="absolute -bottom-32 -right-20 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>

        <div className="rounded-3xl bg-white border border-slate-200 shadow-xl p-8">
          {stage === "request" && (
            <>
              <div className="mx-auto h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-4">
                <KeyRound className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
                Forgot password?
              </h1>
              <p className="mt-1.5 text-sm text-slate-600 text-center">
                Enter your admin email. We&apos;ll send a 6-digit code to your registered WhatsApp number to verify it&apos;s you.
              </p>

              <form onSubmit={requestOtp} className="mt-6 space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="fp-email">Admin email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="fp-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email-input"
                      className="pl-9"
                      placeholder="admin@wappit.com"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                  className="w-full rounded-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send WhatsApp OTP"}
                </Button>
              </form>

              <Link to="/admin/login" className="mt-6 flex items-center justify-center text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="forgot-back-to-login">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to sign in
              </Link>
            </>
          )}

          {stage === "new-password" && (
            <>
              <div className="mx-auto h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-4">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
                Set a new password
              </h1>
              <p className="mt-1.5 text-sm text-slate-600 text-center">
                Identity verified. Choose a strong password (minimum 8 characters).
              </p>

              <form onSubmit={submitNewPassword} className="mt-6 space-y-4" data-testid="new-password-form">
                <div>
                  <Label htmlFor="np">New password</Label>
                  <Input id="np" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5" data-testid="new-password-input" />
                </div>
                <div>
                  <Label htmlFor="cp">Confirm password</Label>
                  <Input id="cp" type="password" required minLength={8} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="mt-1.5" data-testid="confirm-password-input" />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-new-password-btn"
                  className="w-full rounded-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            </>
          )}

          {stage === "done" && (
            <div className="text-center py-6" data-testid="forgot-success">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Password updated
              </h2>
              <p className="mt-2 text-sm text-slate-600">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </div>

      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        mobileMasked={mobileMasked.slice(-4)}
        title="Verify your identity"
        description={<>We&apos;ve sent a 6-digit code to your WhatsApp ending in <b>{mobileMasked.slice(-4)}</b></>}
        onVerify={onVerifyOtp}
        onResend={onResendOtp}
        onChangeNumber={() => { setOtpOpen(false); setStage("request"); }}
        testIdPrefix="forgot-otp"
      />
    </main>
  );
}
